import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, map, takeUntil } from 'rxjs';
import { Product } from '../../../core/models/product.model';
import { CategoryNamePipe } from '../../../core/pipes/category-name.pipe';
import { ProductDisplayNamePipe } from '../../../core/pipes/product-display-name.pipe';
import { TranslatePipe } from '../../../core/pipes/t.pipe';
import { MediaSrcDirective } from '../../directives/media-src.directive';
import { MediaSlotComponent } from '../media-slot/media-slot.component';
import { CartFeedbackService } from '../../../core/services/cart-feedback.service';
import { CompareService } from '../../../core/services/compare.service';
import { I18nService } from '../../../core/services/i18n.service';
import { getHttpErrorMessage } from '../../../core/models/api-response.model';
import { WishlistService } from '../../../core/services/wishlist.service';
import { isProductOutOfStock } from '../../../core/utils/product-stock.util';
import { isProductNew } from '../../../core/utils/product-new.util';
import { NewArrivalsBadgeService } from '../../../core/services/new-arrivals-badge.service';
@Component({
  selector: 'app-product-card',
  standalone: true,
  imports: [CommonModule, RouterModule, CategoryNamePipe, ProductDisplayNamePipe, TranslatePipe, MediaSrcDirective, MediaSlotComponent],
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input({ required: true }) product!: Product;
  /** Brief cart-add feedback (button → “Added”). Not the New Arrivals badge. */
  @Input() recentlyAdded = false;
  /** Force the green “New / جديد” badge (e.g. products in وصل حديثاً). */
  @Input() forceNew = false;
  @Input() showWishlist = true;
  @Input() showCompare = true;
  /** First visible row on homepage rails — higher fetch priority for LCP-adjacent cards. */
  @Input() priorityImage = false;

  @Output() addToCart = new EventEmitter<number>();
  @Output() buyNow = new EventEmitter<number>();
  @Output() quickPreview = new EventEmitter<Product>();
  @Output() wishlistChanged = new EventEmitter<{ productId: number; isInWishlist: boolean }>();

  inWishlist = false;
  inCompare = false;
  wishlistBusy = false;
  private readonly destroy$ = new Subject<void>();

  get isOutOfStock(): boolean {
    return isProductOutOfStock(this.product);
  }

  get isNew(): boolean {
    return (
      this.forceNew ||
      this.newArrivalsBadge.has(this.product?.id) ||
      isProductNew(this.product)
    );
  }

  get buttonLabel(): string {
    if (this.isOutOfStock) {
      return this.i18n.t('product.available_soon');
    }
    return this.recentlyAdded ? this.i18n.t('common.added') : this.i18n.t('common.add_to_cart');
  }

  /** Optional admin-editable badge (e.g. "Best Seller"), localized per the active language. */
  get tagLabel(): string {
    return this.i18n.localizeProductTag(this.product?.tag, this.product?.tagEn);
  }

  constructor(
    private readonly wishlistService: WishlistService,
    private readonly compareService: CompareService,
    private readonly cartFeedback: CartFeedbackService,
    private readonly i18n: I18nService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly newArrivalsBadge: NewArrivalsBadgeService
  ) {}

  ngOnInit(): void {
    this.syncCompareState();
    this.syncWishlistState();
    this.newArrivalsBadge.changes$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    this.wishlistService.wishlistIds$.pipe(takeUntil(this.destroy$)).subscribe((ids) => {
      const next = ids.has(this.product?.id ?? 0);
      if (next !== this.inWishlist) {
        this.inWishlist = next;
        if (this.product) {
          this.product.isInWishlist = next;
        }
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product']) {
      this.syncWishlistState();
      this.syncCompareState();
    }
  }

  private syncWishlistState(): void {
    const fromApi = !!this.product?.isInWishlist;
    this.inWishlist = fromApi || this.wishlistService.isInWishlist(this.product?.id ?? 0);
  }

  private syncCompareState(): void {
    this.inCompare = this.compareService.has(this.product?.id ?? 0);
  }

  onAddToCart(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOutOfStock) {
      this.cartFeedback.showInfo(this.i18n.t('product.available_soon'));
      return;
    }
    this.addToCart.emit(this.product.id);
  }

  onBuyNow(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOutOfStock) {
      this.cartFeedback.showInfo(this.i18n.t('product.available_soon'));
      return;
    }
    this.buyNow.emit(this.product.id);
  }

  onQuickPreview(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.quickPreview.emit(this.product);
  }

  onCompareClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.compareService.has(this.product.id)) {
      void this.router.navigate(['/compare']);
      return;
    }
    const ok = this.compareService.add(this.product.id);
    if (!ok) {
      this.cartFeedback.showError(this.i18n.t('product.compare_full'));
      return;
    }
    this.inCompare = true;
    this.cartFeedback.showSuccess(this.i18n.t('product.compare_added'));
    this.cdr.markForCheck();
  }

  onWishlistClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.wishlistBusy) {
      return;
    }
    const wasInWishlist = this.inWishlist;
    // Optimistic flip — service patches shared state immediately.
    this.inWishlist = !wasInWishlist;
    this.product.isInWishlist = this.inWishlist;
    this.wishlistChanged.emit({ productId: this.product.id, isInWishlist: this.inWishlist });
    this.cartFeedback.showSuccess(
      this.i18n.t(this.inWishlist ? 'wishlist.added' : 'wishlist.removed')
    );
    this.cdr.markForCheck();

    const request$ = wasInWishlist
      ? this.wishlistService.remove(this.product.id).pipe(map(() => void 0))
      : this.wishlistService.add(this.product.id, this.product).pipe(map(() => void 0));

    this.wishlistBusy = true;
    request$.subscribe({
      next: () => {
        this.wishlistBusy = false;
        this.cdr.markForCheck();
      },
      error: (error: unknown) => {
        this.wishlistBusy = false;
        const message = getHttpErrorMessage(error, this.i18n.t('wishlist.error'));
        const alreadyListed =
          /already in wishlist/i.test(message) || /already exists/i.test(message);
        if (alreadyListed && !wasInWishlist) {
          this.inWishlist = true;
          this.product.isInWishlist = true;
          this.wishlistChanged.emit({ productId: this.product.id, isInWishlist: true });
          this.cartFeedback.showSuccess(this.i18n.t('wishlist.added'));
          this.cdr.markForCheck();
          return;
        }
        // Rollback UI — service already restored snapshot.
        this.inWishlist = wasInWishlist;
        this.product.isInWishlist = wasInWishlist;
        this.wishlistChanged.emit({ productId: this.product.id, isInWishlist: wasInWishlist });
        this.cartFeedback.showError(message);
        this.cdr.markForCheck();
      },
    });
  }
}
